import React, { useEffect, useState } from 'react';
import { Question, isQuestionContent, MarkschemeType } from "@/types/test";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Trash2, ClockIcon, AwardIcon, HelpCircleIcon, RefreshCw, BookmarkIcon, FileTextIcon } from "lucide-react";
import { QuestionTypeSelector } from "./question-types/QuestionTypeSelector";
import { QuestionContent } from "./question-types/QuestionContent";
import { MultipleChoiceEditor } from "./question-types/MultipleChoiceEditor";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { toast } from "@/components/ui/use-toast";
import { useIsMobile } from "@/hooks/use-mobile";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { MarkschemeDialog } from "@/components/shared/MarkschemeDialog";
import { NumericInputWithPresets } from "./NumericInputWithPresets";
import { RegenerationFeedbackDialog } from "./RegenerationFeedbackDialog";
import { ValidationSummaryCard } from "./validation/ValidationSummaryCard";
import { ValidatedInput } from "./validation/ValidatedInput";
import { ValidatedNumericInput } from "./validation/ValidatedNumericInput";
import { validateQuestion } from "./utils/questionValidation";

interface QuestionEditorProps {
  question: Question;
  questionNumber?: number;
  onUpdate: (id: string, updates: Partial<Question>) => void;
  onDelete: (id: string) => void;
  onRegenerate?: (id: string, feedback: string) => Promise<void>;
  onDoneEditing?: () => void; // New prop for done editing callback
  jobContext?: {
    title?: string;
    description?: string;
    keySkills?: string[];
    yearsOfExperience?: string;
  };
}

const QuestionEditor = React.memo(({ 
  question, 
  questionNumber, 
  onUpdate, 
  onDelete,
  onRegenerate,
  onDoneEditing,
  jobContext
}: QuestionEditorProps) => {
  console.log('📝 Memoized QuestionEditor rendering for:', question.id);
  const [isRegenerating, setIsRegenerating] = useState(false);
  const [markschemeDialogOpen, setMarkschemeDialogOpen] = useState(false);
  const [feedbackDialogOpen, setFeedbackDialogOpen] = useState(false);
  const isMobile = useIsMobile();

  useEffect(() => {
    if (!question.time_limit) {
      let defaultTimeLimit = 60;
      if (question.question_type === "essay") {
        defaultTimeLimit = 300;
      } else if (question.question_type === "audio_response" || question.question_type === "video_response") {
        defaultTimeLimit = 180;
      } else if (question.question_type === "coding") {
        defaultTimeLimit = 1800; // 30 minutes for coding questions
      }
      onUpdate(question.id, { time_limit: defaultTimeLimit });
    }
  }, [question.id, question.time_limit, question.question_type, onUpdate]);

  const handleTypeChange = (type: Question["question_type"]) => {
    console.log(`🔄 Changing question type to: ${type}`);
    const questionContent = isQuestionContent(question.content) ? question.content : { question: '' };
    let newContent = {
      ...questionContent,
    };
    
    if (type === "multiple_choice") {
      newContent = {
        ...newContent,
        options: questionContent.options || [""],
        correctOption: questionContent.correctOption || 0,
      };
    } else {
      const { options, correctOption, ...rest } = newContent;
      newContent = rest;
    }
    
    let newTimeLimit = question.time_limit;
    if (type === "multiple_choice") {
      newTimeLimit = 60;
    } else if (type === "essay") {
      newTimeLimit = 300;
    } else if (type === "audio_response" || type === "video_response") {
      newTimeLimit = 180;
    } else if (type === "coding") {
      newTimeLimit = 1800; // 30 minutes for coding questions
    }
    
    console.log('📤 Updating question with new content:', { type, newContent });
    onUpdate(question.id, { 
      question_type: type, 
      content: newContent,
      time_limit: newTimeLimit
    });
  };

  const handleContentUpdate = (content: Question["content"]) => {
    console.log('📝 Updating question content:', content);
    onUpdate(question.id, { content });
  };

  const handleRegenerateWithFeedback = async (feedback: string) => {
    if (!onRegenerate) return;
    
    try {
      setIsRegenerating(true);
      await onRegenerate(question.id, feedback);
      toast({
        title: "Success",
        description: "Question regenerated successfully with your feedback",
      });
    } catch (error) {
      console.error('Error regenerating question:', error);
      toast({
        title: "Error",
        description: "Failed to regenerate question. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsRegenerating(false);
    }
  };

  const handleRegenerateClick = () => {
    setFeedbackDialogOpen(true);
  };

  const handleMarkschemeUpdate = (markscheme: MarkschemeType) => {
    onUpdate(question.id, { 
      markscheme, 
      marks: markscheme.total_points || 1
    });
    setMarkschemeDialogOpen(false);
  };

  const timePresets = [
    { value: 30, label: "30 seconds" },
    { value: 60, label: "60 seconds" },
    { value: 90, label: "90 seconds" },
    { value: 120, label: "2 minutes" },
    { value: 180, label: "3 minutes" },
    { value: 300, label: "5 minutes" },
    { value: 600, label: "10 minutes" },
  ];

  const markPresets = [
    { value: 1, label: "1 mark" },
    { value: 2, label: "2 marks" },
    { value: 3, label: "3 marks" },
    { value: 4, label: "4 marks" },
    { value: 5, label: "5 marks" },
    { value: 10, label: "10 marks" },
  ];

  const difficultyOptions = [
    { value: 'easy', label: 'Easy' },
    { value: 'medium', label: 'Medium' },
    { value: 'hard', label: 'Hard' },
  ];

  const isValid = validateQuestion(question);
  const hasQuestionText = isQuestionContent(question.content) && question.content.question?.trim();
  const hasTimeLimit = question.time_limit && question.time_limit > 0;
  const hasMarks = question.marks && question.marks > 0;
  const hasMarkscheme = question.markscheme && question.markscheme.criteria && question.markscheme.criteria.length > 0;

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <div className="flex items-center w-full">
          <span className="font-bold text-lg mr-4 text-foreground">Q{questionNumber || '?'}</span>
          <div className="flex items-center gap-2 ml-auto">
            {onDoneEditing && (
              <Button
                variant="default"
                size="sm"
                onClick={onDoneEditing}
                className="bg-green-600 hover:bg-green-700 text-white"
              >
                Done Editing
              </Button>
            )}
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => setMarkschemeDialogOpen(true)}
                    className="text-foreground hover:text-foreground hover:bg-transparent"
                  >
                    <FileTextIcon className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>{question.markscheme ? "View Markscheme" : "Create Markscheme"}</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
            {onRegenerate && (
              <Button
                variant="outline"
                size="icon"
                onClick={handleRegenerateClick}
                disabled={isRegenerating}
                className="text-foreground hover:text-foreground hover:bg-transparent"
              >
                <RefreshCw className={`h-4 w-4 ${isRegenerating ? 'animate-spin' : ''}`} />
              </Button>
            )}
            <Button
              variant="ghost"
              size="icon"
              onClick={() => onDelete(question.id)}
              className="text-[#8E9196] hover:text-[#ea384c]"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className={`grid ${isMobile ? 'grid-cols-2 gap-3' : 'grid-cols-4 gap-4'}`}>
          <div className="space-y-2">
            <Label className="flex items-center gap-1 text-sm font-medium text-foreground">
              <HelpCircleIcon className="h-4 w-4" />
              Question Type
            </Label>
            <QuestionTypeSelector
              value={question.question_type}
              onChange={handleTypeChange}
              className="bg-card"
            />
          </div>
          
          <ValidatedNumericInput
            label="Time Limit"
            value={question.time_limit}
            onChange={(value) => onUpdate(question.id, { time_limit: value })}
            presets={timePresets}
            placeholder="Time in seconds"
            className="w-full"
            required
            icon={<ClockIcon className="h-4 w-4" />}
            error="Set how long candidates have to answer"
          />
          
          <ValidatedNumericInput
            label="Marks"
            value={question.marks}
            onChange={(value) => onUpdate(question.id, { marks: value })}
            presets={markPresets}
            placeholder="Number of marks"
            className="w-full"
            required
            icon={<AwardIcon className="h-4 w-4" />}
            error="Set how many marks this question is worth"
          />
          
          <div className="space-y-2">
            <Label className="flex items-center gap-1 text-sm font-medium text-foreground">
              Difficulty
            </Label>
            <Select 
              value={question.difficulty || "not-specified"} 
              onValueChange={(value) => 
                onUpdate(question.id, {
                  difficulty: value === "not-specified" ? null : value as 'easy' | 'medium' | 'hard',
                })
              }
            >
              <SelectTrigger className={`w-full ${isMobile ? 'h-12' : ''} bg-card`}>
                <SelectValue placeholder="Select difficulty" />
              </SelectTrigger>
              <SelectContent className="bg-card">
                <SelectItem value="not-specified">Not specified</SelectItem>
                {difficultyOptions.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div>
          <ValidatedInput
            label="Skill"
            value={question.skill || ''}
            onChange={(value) => onUpdate(question.id, { skill: value || null })}
            placeholder="Enter skill (e.g., JavaScript, UI Design)"
            className="bg-card"
          />
        </div>

        <div className="space-y-2">
          <Label className={`flex items-center gap-1 text-sm font-medium ${!hasQuestionText ? 'text-red-600 dark:text-red-400' : 'text-foreground'}`}>
            <HelpCircleIcon className="h-4 w-4" />
            Question Text
            <span className="text-red-500">*</span>
          </Label>
          <QuestionContent
            content={question.content}
            onUpdate={(content) => handleContentUpdate(content)}
            question={question}
            isEditing={true}
            onQuestionUpdate={onUpdate}
          />
          {!hasQuestionText && (
            <p className="text-xs text-red-600 dark:text-red-400 flex items-center gap-1">
              <div className="w-1 h-1 bg-red-500 rounded-full" />
              Enter the question text that candidates will see
            </p>
          )}
        </div>

        {/* Validation Summary Card */}
        <ValidationSummaryCard 
          question={question}
          questionNumber={questionNumber || 1}
          isValid={isValid}
        />

        {question.question_type === "multiple_choice" && (
          <MultipleChoiceEditor
            content={question.content}
            onUpdate={(content) => handleContentUpdate(content)}
          />
        )}

        <MarkschemeDialog
          questionNumber={questionNumber}
          markscheme={question.markscheme}
          open={markschemeDialogOpen}
          onOpenChange={setMarkschemeDialogOpen}
          onSave={handleMarkschemeUpdate}
          question={question}
          jobContext={jobContext}
        />

        <RegenerationFeedbackDialog
          open={feedbackDialogOpen}
          onOpenChange={setFeedbackDialogOpen}
          question={question}
          questionNumber={questionNumber}
          onRegenerate={handleRegenerateWithFeedback}
        />

      </CardContent>
    </Card>
  );
});

QuestionEditor.displayName = 'QuestionEditor';

export default QuestionEditor;
